import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { format, parseISO } from 'date-fns/esm'
import { nl } from 'date-fns/locale'
import styled, { useTheme } from 'styled-components'

import { Card } from '@/@bothrs-ui/web/components/card/Card'
import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { Skeleton } from '@/@bothrs-ui/web/components/skeleton/Skeleton'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { capitalize } from '@/modules/shared/helpers/locale'
import { DoctorInfo } from '../../components/doctor-info/DoctorInfo'

import type { SlotWithLocationAndDoctor } from '../types/booking.type'

type SlotCardProps = {
  slot: SlotWithLocationAndDoctor
  onSlotSelect: (
    action: 'select-slot',
    data?: {
      slot: SlotWithLocationAndDoctor
    },
  ) => void
  isLoading: boolean
}

export const SlotCard = ({ slot, onSlotSelect, isLoading }: SlotCardProps) => {
  const theme = useTheme()

  const date = parseISO(slot.start)
  const formattedDate = capitalize(format(date, 'EEEE d/MM/yyyy', { locale: nl }))
  const formattedTime = format(date, 'HH:mm', { locale: nl })

  return (
    <StSlotCardButton
      type='button'
      disabled={isLoading}
      onClick={() => {
        onSlotSelect('select-slot', { slot })
      }}
    >
      <StSlotCard>
        <FlexRow justifyContent='space-between' alignItems='flex-end'>
          <FlexColumn gap={theme.UI.SpacingPx.Space2}>
            <Span type='BodyMediumRegular'>
              {`${formattedDate} · `}
              <Span type='BodyMediumBold'>{formattedTime}</Span>
            </Span>
            <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Dark}>
              {`${slot.location?.address} · ${slot.location?.name}`}
            </Span>
            {slot.doctor ? <DoctorInfo doctor={slot.doctor} /> : null}
          </FlexColumn>

          <StChevron aria-hidden='true'>
            <FontAwesomeIcon icon={['far', 'chevron-right']} />
          </StChevron>
        </FlexRow>
      </StSlotCard>
    </StSlotCardButton>
  )
}

export function SlotCardSkeleton() {
  const theme = useTheme()

  return (
    <Card>
      <FlexRow justifyContent='space-between' alignItems='flex-end'>
        <FlexColumn gap={theme.UI.SpacingPx.Space2}>
          <FlexRow gap={theme.UI.SpacingPx.Space2}>
            <Skeleton style={{ width: '147px' }} />
            <Skeleton style={{ width: '47px' }} />
          </FlexRow>
          <FlexRow gap={theme.UI.SpacingPx.Space2}>
            <Skeleton style={{ width: '183px' }} />
          </FlexRow>
          <FlexRow gap={theme.UI.SpacingPx.Space2}>
            <Skeleton style={{ width: '20px' }} />
            <Skeleton style={{ width: '202px' }} />
          </FlexRow>
        </FlexColumn>
        <FlexColumn alignItems='flex-end'>
          <Skeleton style={{ width: '32px', height: '32px' }} />
        </FlexColumn>
      </FlexRow>
    </Card>
  )
}

const StSlotCardButton = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  width: 100%;
  text-align: left;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Large}px;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px
      ${({ theme }) => theme.Colors.Theme.Primary?.[700] ?? theme.Colors.Theme.Primary ?? '#022248'};
  }

  &:disabled {
    cursor: default;
  }
`

const StSlotCard = styled(Card)`
  width: 100%;
`

const StChevron = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  font-size: 16px;
`
